import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Loader2, AlertCircle, RefreshCw } from 'lucide-react';

export default function AuthCallback() {
  const navigate = useNavigate();
  const { exchangeSession, login } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Use ref to prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      // Extract session_id from URL hash
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);

      if (!sessionIdMatch) {
        console.error('[AuthCallback] No session_id found in URL');
        setError({
          type: 'no_session',
          message: 'No session token was returned from Google. Please try signing in again.',
        });
        return;
      }

      const sessionId = sessionIdMatch[1];

      try {
        // Exchange session_id for session token. The backend sets the
        // httpOnly session cookie as part of this response.
        const userData = await exchangeSession(sessionId);

        // Clear the hash so a back-button press can't replay the session_id.
        window.history.replaceState(null, '', window.location.pathname);

        // HARD navigate (not react-router navigate) on purpose. We just set
        // a new auth cookie and brand-new user context — a soft navigate
        // forces the React tree to reconcile during the same render cycle
        // and we've seen it race the AppRouter's hash check, bouncing
        // already-authenticated users back to the landing page. A full
        // document load guarantees: (a) the cookie is sent on the next /me
        // call, (b) AuthProvider runs checkAuth from a clean slate, and
        // (c) AppRouter renders the destination route with a real user.
        const destination = userData?.onboarding_completed ? '/dashboard' : '/onboarding';
        window.location.replace(destination);
      } catch (err) {
        // Surface the specific upstream error so the user knows it's
        // transient, not a permanent app breakage. Most common case:
        // "Invalid state parameter" coming from auth.emergentagent.com
        // when a session_id is reused, expired, or the round-trip cookie
        // got dropped (Safari ITP, third-party cookies blocked, etc).
        const detail =
          err?.response?.data?.detail ||
          err?.message ||
          'Unknown error';
        const status = err?.response?.status;
        console.error('[AuthCallback] Auth exchange failed:', status, detail, err);

        // Always clear the stale hash so a Retry click doesn't replay it.
        window.history.replaceState(null, '', window.location.pathname);

        setError({
          type: 'exchange_failed',
          status,
          message: detail,
        });
      }
    };

    processAuth();
  }, [exchangeSession, navigate]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <div className="max-w-md w-full bg-slate-900 border border-slate-800 rounded-xl p-6 text-center" data-testid="auth-error-card">
          <AlertCircle className="h-12 w-12 mx-auto text-amber-500 mb-3" />
          <h2 className="text-xl font-semibold text-white mb-2">Sign-in didn’t complete</h2>
          <p className="text-slate-400 text-sm mb-1">
            {error.message || 'Authentication failed.'}
          </p>
          {error.status && (
            <p className="text-slate-600 text-xs mb-4">Code: {error.status}</p>
          )}
          <p className="text-slate-500 text-xs mb-5">
            This usually clears up on a fresh attempt. If it keeps failing, try a different
            browser or disable third-party cookie blocking for{' '}
            <code className="px-1 rounded bg-slate-800">auth.emergentagent.com</code>.
          </p>
          <div className="flex gap-2 justify-center">
            <button
              onClick={login}
              data-testid="auth-retry-btn"
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#3A7BFF] hover:bg-[#4c89ff] text-white font-medium transition"
            >
              <RefreshCw className="w-4 h-4" /> Try again
            </button>
            <button
              onClick={() => navigate('/', { replace: true })}
              className="px-5 py-2.5 rounded-lg border border-slate-700 hover:bg-slate-800 text-slate-300 transition"
            >
              Back to home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <Loader2 className="h-12 w-12 animate-spin mx-auto text-accent" />
        <p className="mt-4 text-muted-foreground">Signing you in...</p>
      </div>
    </div>
  );
}
